/**
 * MotoriAiPage — «Motori AI»: chi risponde per ogni compito, e cosa è cambiato da solo.
 *
 * Sola lettura, di proposito (Simone, 17/09/2026): nessuno sceglie un modello, né qui né nel
 * codice. Il registro (`platform_config/modelli_ai`) prende il definitivo più recente di ogni
 * classe, ripiega se un modello viene ritirato, usa un preview solo come PROVVISORIO quando
 * l'ultima generazione non ha ancora il definitivo, e lo promuove da solo appena esce. Questa
 * pagina risponde a tre domande: cosa c'è in uso e da quando; è vivo (controllo delle 06:20);
 * cosa è cambiato senza che nessuno lo decidesse.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { Memory } from '@mui/icons-material';
import { tokens } from '../theme';
import { SoftChip, type SoftChipTone } from '../components/SoftChip';
import { MetricNumber } from '../components/design/MetricNumber';
import { osservaDestinatariAvvisiPiattaforma } from '../services/avvisiPiattaformaService';
import {
  COSA_FA_LA_FAMIGLIA,
  NOME_FAMIGLIA,
  NOME_FORNITORE,
  NOME_PERCHE,
  NOME_RUOLO,
  NOME_SCELTA,
  giorniAlRitiro,
  osservaRegistroModelli,
  righeDelRegistro,
  saluteDellaVoce,
  storicoUnito,
  type DocumentoRegistro,
  type Famiglia,
} from '../services/motoriAiService';

const mono = { fontFamily: tokens.font.mono, fontVariantNumeric: 'tabular-nums' } as const;

function quandoIso(iso: string | undefined | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function tonoPerche(perche: string): SoftChipTone {
  if (perche === 'ripiego' || perche === 'cambio_chiave') return 'warning';
  if (perche === 'promozione') return 'success';
  if (perche === 'mano') return 'info';
  return 'default';
}

export const MotoriAiPage: React.FC = () => {
  const [documento, setDocumento] = useState<DocumentoRegistro | null | undefined>(undefined);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => osservaRegistroModelli(setDocumento, setErrore), []);
  // Chi riceve gli avvisi: l'elenco UNICO della piattaforma (05/10/2026), non più `modelli_ai.avvisi`.
  const [destinatariAvvisi, setDestinatariAvvisi] = useState<string[] | undefined>(undefined);
  useEffect(() => osservaDestinatariAvvisiPiattaforma(setDestinatariAvvisi, setErrore), []);

  const righe = useMemo(() => righeDelRegistro(documento), [documento]);
  const storico = useMemo(() => storicoUnito(documento), [documento]);
  const controllo = documento?.controllo?.ultimo;
  const oggi = new Date();

  const conteggi = useMemo(() => {
    const salute = righe.map((r) => saluteDellaVoce(r, controllo));
    return {
      voci: righe.length,
      vivi: salute.filter((s) => s.salute === 'vivo').length,
      guasti: salute.filter((s) => s.salute === 'guasto').length,
      provvisori: righe.filter((r) => r.voce.provvisorio).length,
      cambiDaSoli: storico.filter((s) => s.perche === 'ripiego' || s.perche === 'promozione').length,
    };
  }, [righe, controllo, storico]);

  const perFamiglia = useMemo(() => {
    const m = new Map<Famiglia, typeof righe>();
    for (const r of righe) m.set(r.famiglia, [...(m.get(r.famiglia) ?? []), r]);
    return [...m.entries()];
  }, [righe]);

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2} mb={3}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: tokens.font.display, fontWeight: 600, fontSize: { xs: '1.6rem', md: '1.95rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: tokens.color.graphite, fontVariationSettings: '"WONK" 0, "SOFT" 30' }}>
            Motori AI
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.ash, mt: 0.5, maxWidth: 640 }}>
            Chi risponde per ogni compito. Il sistema sceglie da solo il modello definitivo più recente, ripiega se uno viene ritirato e avvisa per email: qui si guarda, non si cambia.
          </Typography>
        </Box>
      </Stack>

      {errore && <Alert severity="error" sx={{ mb: 2 }}>{errore}</Alert>}

      {documento === undefined ? (
        <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : documento === null ? (
        <Alert severity="warning">Il registro dei modelli non esiste ancora: va seminato da <code>functions/scripts/seed-registro-modelli.ts --apply</code>.</Alert>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 2, sm: 5 }}>
              <MetricNumber value={conteggi.voci} label="Compiti coperti" />
              <MetricNumber value={conteggi.vivi} label="Vivi all'ultimo controllo" color={conteggi.guasti ? tokens.color.warning : tokens.color.success} caption={conteggi.guasti ? `${conteggi.guasti} non rispondono` : 'tutti rispondono'} />
              <MetricNumber value={conteggi.provvisori} label="Provvisori" caption={conteggi.provvisori ? 'preview in attesa del definitivo' : 'nessun preview in uso'} />
              <MetricNumber value={conteggi.cambiDaSoli} label="Cambi avvenuti da soli" caption="ripieghi e promozioni, da sempre" />
            </Stack>
            <Typography variant="caption" sx={{ display: 'block', mt: 2, color: tokens.color.mist }}>
              Ultimo controllo del mattino: {controllo?.quando ? controllo.quando.toDate().toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'mai'}
              {controllo?.catalogo_voci !== undefined ? ` · ${controllo.catalogo_voci} modelli nel catalogo dei fornitori` : ''}
              {controllo?.avvisi?.length ? ` · ${controllo.avvisi.length} avvisi${controllo.email_inviata ? ', email inviata' : ', email NON inviata'}` : ' · niente da segnalare'}
              {destinatariAvvisi === undefined ? '' : destinatariAvvisi.length ? ` · avvisi a ${destinatariAvvisi.join(', ')}` : ' · ⚠️ nessun destinatario per gli avvisi (platform_config/avvisi_piattaforma)'}
            </Typography>
          </Paper>

          {perFamiglia.map(([famiglia, vociFamiglia]) => (
            <Paper key={famiglia} variant="outlined" sx={{ mb: 2 }}>
              <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Memory fontSize="small" sx={{ color: tokens.color.ash }} />
                  <Typography variant="subtitle1" fontWeight={700}>{NOME_FAMIGLIA[famiglia]}</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: tokens.color.mist }}>{COSA_FA_LA_FAMIGLIA[famiglia]}</Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Compito</TableCell>
                    <TableCell>Modello in uso</TableCell>
                    <TableCell>Stato</TableCell>
                    <TableCell>Da quando</TableCell>
                    <TableCell>Ultimo controllo</TableCell>
                    <TableCell>Ritiro annunciato</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vociFamiglia.map((r) => {
                    const s = saluteDellaVoce(r, controllo);
                    const giorni = giorniAlRitiro(r.voce.ritiro_previsto, oggi);
                    return (
                      <TableRow key={`${r.famiglia}/${r.ruolo}`} hover>
                        <TableCell><Typography variant="body2" fontWeight={600}>{NOME_RUOLO[r.ruolo]}</Typography></TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" sx={mono}>{r.voce.modello}</Typography>
                            <Typography variant="caption" sx={{ color: tokens.color.mist }}>{NOME_FORNITORE[r.voce.fornitore ?? 'google'] ?? r.voce.fornitore} · {r.voce.regione}</Typography>
                            {r.voce.provvisorio ? (
                              <Tooltip title="È un preview: l'ultima generazione non ha ancora un definitivo per questa classe. Il controllo del mattino lo sostituirà da solo appena esce."><span><SoftChip label="provvisorio" tone="warning" /></span></Tooltip>
                            ) : (
                              <SoftChip label="definitivo" tone="default" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {s.salute === 'vivo' && <SoftChip label={s.ms !== undefined ? `risponde · ${(s.ms / 1000).toFixed(1)} s` : 'risponde'} tone="success" />}
                          {s.salute === 'guasto' && <Tooltip title={s.errore ?? ''}><span><SoftChip label="non risponde" tone="error" /></span></Tooltip>}
                          {s.salute === 'mai_provato' && <SoftChip label="mai provato" tone="default" outlined />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={mono}>{quandoIso(r.voce.scelto_il)}</Typography>
                          <Typography variant="caption" sx={{ color: tokens.color.mist }}>{NOME_SCELTA[r.voce.scelto_da] ?? r.voce.scelto_da}{r.voce.motivo ? ` · ${r.voce.motivo}` : ''}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...mono, color: tokens.color.ash }}>{quandoIso(r.voce.verificato_il)}</TableCell>
                        <TableCell>
                          {giorni === null ? (
                            <Typography variant="caption" sx={{ color: tokens.color.mist }}>non indicato</Typography>
                          ) : (
                            <SoftChip label={giorni < 0 ? `ritirato il ${r.voce.ritiro_previsto}` : `${r.voce.ritiro_previsto} · fra ${giorni} giorni`} tone={giorni <= 30 ? 'error' : giorni <= 60 ? 'warning' : 'default'} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Paper>
          ))}

          <Paper variant="outlined">
            <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>Cosa è cambiato</Typography>
              <Typography variant="caption" sx={{ color: tokens.color.mist }}>Ogni riga è una scelta del registro: semina, ripiego dopo un ritiro, promozione dal provvisorio al definitivo, scelta a mano, o passaggio a una chiave di riserva perché la prima ha finito i crediti o è stata rifiutata.</Typography>
            </Box>
            {storico.length === 0 ? (
              <Typography variant="body2" sx={{ p: 2.5, color: tokens.color.mist }}>Nessun cambiamento registrato.</Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Quando</TableCell>
                    <TableCell>Compito</TableCell>
                    <TableCell>Da</TableCell>
                    <TableCell>A</TableCell>
                    <TableCell>Perché</TableCell>
                    <TableCell>Dettaglio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storico.map((s, i) => (
                    <TableRow key={`${s.famiglia}/${s.ruolo}/${s.quando}/${i}`} hover>
                      <TableCell sx={{ ...mono, whiteSpace: 'nowrap' }}>{quandoIso(s.quando)}</TableCell>
                      <TableCell><Typography variant="body2">{NOME_FAMIGLIA[s.famiglia]} · {NOME_RUOLO[s.ruolo] ?? s.ruolo}</Typography></TableCell>
                      <TableCell sx={{ ...mono, color: tokens.color.mist }}>{s.da ?? '—'}</TableCell>
                      <TableCell sx={mono}>{s.a}</TableCell>
                      <TableCell><SoftChip label={NOME_PERCHE[s.perche] ?? s.perche} tone={tonoPerche(s.perche)} /></TableCell>
                      <TableCell><Typography variant="caption" sx={{ color: tokens.color.ash }}>{s.motivo ?? ''}</Typography></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default MotoriAiPage;
